import {
  PropsWithChildren,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMount, useUpdateEffect } from "react-use";
import usePrevious from "react-use/lib/usePrevious";

import { useContextSupportMessenger } from "@/context/support_messenger";
import { useCustomLocalStorage } from "@/hooks/use_custom_local_storage";
import { useDialog } from "@/hooks/use_dialog";
import { useEpOrganizationOrganizationList } from "@/hooks/use_ep_organization";
import { DialogNoCreationPermissions } from "@/screens/app/components/dialogs/dialog_no_creation_permissions";
import { DialogPc } from "@/screens/app/components/dialogs/dialog_pc";
import { DialogPcV1 } from "@/screens/app/components/dialogs/dialog_pc_v1";
import { useContextApp } from "@/screens/app/context/app";
import { generateNewPostUrl } from "@/screens/app/screens/post/helpers/generate_new_post_url";

import { ContextPostConstructorOpener } from "./post_constructor_opener.context";
import { IContextPostConstructorOpener } from "./post_constructor_opener.types";

export const ProviderPostConstructorOpener = ({
  children,
}: PropsWithChildren) => {
  const navigate = useNavigate();
  const location = useLocation();
  const prevLocation = usePrevious(location);
  const {
    actions: { carrotTrack },
  } = useContextSupportMessenger();

  const [availableVersions, setAvailableVersions] = useState([1, 2, 3]);

  const [fallbackVersion, setFallbackVersion] = useState(availableVersions[0]);

  const [postConstructorVersionSelected] = useCustomLocalStorage(
    "ep.post_constructor.version",
    fallbackVersion,
  );

  const {
    state: { organizationId },
  } = useContextApp();

  const { organizationList } = useEpOrganizationOrganizationList();

  const [isOpen, setIsOpen] = useState(false);

  const [formParams, setFormParams] = useState<
    Parameters<IContextPostConstructorOpener["actions"]["open"]>[0] | undefined
  >();

  const postConstructorVersion = useMemo(() => {
    if (
      formParams?.initialText !== undefined ||
      formParams?.mediaSet !== undefined
    ) {
      return 3;
    }

    if (availableVersions.includes(postConstructorVersionSelected)) {
      return postConstructorVersionSelected;
    }

    return fallbackVersion;
  }, [
    formParams?.initialText,
    formParams?.mediaSet,
    availableVersions,
    postConstructorVersionSelected,
    fallbackVersion,
  ]);

  const organization = useMemo(
    () =>
      organizationList &&
      organizationList.find(
        (organization) => organization.id === organizationId,
      ),
    [organizationId, organizationList],
  );

  const {
    close: closeNoPermissionsDialog,
    open: openNoPermissionsDialog,
    isOpen: isNoPermissionsDialogOpen,
  } = useDialog();

  const open = useCallback<IContextPostConstructorOpener["actions"]["open"]>(
    (params) => {
      if (organization?.role === "observer_v1") {
        return openNoPermissionsDialog();
      }

      carrotTrack && carrotTrack("schedule_post_btn", {});

      setFormParams({
        ...params,
        initialText:
          params?.initialText && postConstructorVersion === 3
            ? params.initialText.replace(/\n\n/g, "\n\n\n")
            : params?.initialText,
      });
      setIsOpen(true);
    },
    [
      carrotTrack,
      openNoPermissionsDialog,
      organization?.role,
      postConstructorVersion,
    ],
  );

  const handleChangeRoute = useCallback(() => {
    closeNoPermissionsDialog();
  }, [closeNoPermissionsDialog]);

  useLayoutEffect(() => {
    if (isOpen && postConstructorVersion === 2) {
      setIsOpen(false);

      const { pathname, state } = generateNewPostUrl(formParams);

      navigate(pathname, { state });
    }
  }, [formParams, isOpen, navigate, postConstructorVersion]);

  useUpdateEffect(() => {
    if (!isOpen) {
      setFormParams(undefined);
    }
  }, [isOpen]);

  useMount(() => {
    if (prevLocation?.pathname !== location.pathname) {
      handleChangeRoute();
    }
  });

  return (
    <ContextPostConstructorOpener.Provider
      value={{
        actions: {
          open,
          setAvailableVersions,
          setFallbackVersion,
        },
      }}
    >
      {children}

      <DialogPcV1
        isOpen={isOpen && postConstructorVersion === 1}
        onClose={() => setIsOpen(false)}
        {...formParams}
      />

      <DialogPc
        isOpen={isOpen && postConstructorVersion === 3}
        onClose={() => setIsOpen(false)}
        {...formParams}
      />

      <DialogNoCreationPermissions
        isOpen={isNoPermissionsDialogOpen}
        closeDialog={closeNoPermissionsDialog}
      />
    </ContextPostConstructorOpener.Provider>
  );
};

export const useContextPostConstructorOpener = (
  params?: Partial<{
    versions: Array<1 | 2 | 3>;
    fallbackVersion: 1 | 2 | 3;
  }>,
) => {
  const context = useContext(ContextPostConstructorOpener);

  useMount(() => {
    if (params?.versions) {
      context.actions.setAvailableVersions(params.versions);
    }

    if (params?.fallbackVersion) {
      context.actions.setFallbackVersion(params.fallbackVersion);
    }
  });

  return context;
};
